import { Avatar, AvatarFallback, AvatarImage } from '@monksflow/monks-ui';
import { UsersIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { TagInput } from '@/shared/ui';
import { getInitials } from '@/shared/utils/getInitials';

import type { ApproverOption } from './ApproverPicker.types';
import { assigneeLabel } from './approver-options.utils';

/** `onRemove` is left out for a chip the caller may not remove. */
export function ApproverChip(props: { option: ApproverOption; onRemove?: () => void }) {
  const { t } = useTranslation('approvalChains');
  const { option } = props;

  if (option.kind === 'role') {
    return (
      <TagInput.Chip
        avatar={<UsersIcon aria-hidden className="size-3.5" />}
        label={option.role.name}
        {...removableBy(
          props.onRemove,
          t('manageApprovalChains.approverPicker.removeRole', { name: option.role.name }),
        )}
      />
    );
  }

  const label = assigneeLabel(option.person);
  return (
    <TagInput.Chip
      title={option.person.email}
      avatar={
        <Avatar size="xs">
          {option.person.avatarUrl && (
            <AvatarImage src={option.person.avatarUrl} alt={option.person.name} />
          )}
          <AvatarFallback size="xs">{getInitials(label)}</AvatarFallback>
        </Avatar>
      }
      label={label}
      {...removableBy(
        props.onRemove,
        t('manageApprovalChains.approvalChainStepRow.removeApprover', { name: label }),
      )}
    />
  );
}

/** A chip's remove props: the handler with its button's name, or neither. */
function removableBy(onRemove: (() => void) | undefined, removeLabel: string) {
  return onRemove ? { onRemove, removeLabel } : {};
}

export function ApproverOptionRow(props: { option: ApproverOption }) {
  const { t } = useTranslation('approvalChains');
  const { option } = props;

  switch (option.kind) {
    case 'role':
      return (
        <div className="flex items-center gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-tertiary">
            <UsersIcon aria-hidden className="size-4" />
          </span>
          <div className="flex flex-col">
            <span className="text-sm text-text-primary">{option.role.name}</span>
            <span className="text-xs text-text-secondary">
              {t('manageApprovalChains.approverPicker.anyoneWithThisRole')}
            </span>
          </div>
        </div>
      );
    case 'person': {
      const label = assigneeLabel(option.person);
      return (
        <div className="flex items-center gap-3">
          <Avatar className="size-8">
            {option.person.avatarUrl && (
              <AvatarImage src={option.person.avatarUrl} alt={option.person.name} />
            )}
            <AvatarFallback>{getInitials(label)}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm text-text-primary">{label}</span>
            {option.person.email && (
              <span className="text-xs text-text-secondary">{option.person.email}</span>
            )}
          </div>
        </div>
      );
    }
  }
}
